/**
 * Modal "Downloads prontos" — apresentado apos o BotaoDownload requisitar
 * N >= 2 ZIPs do backend windx (POST /produtos/windx/download/zip/).
 *
 * Por que existe (decisao tomada em 2026-05-15 apos investigacao do bug
 * de multi-download em QA):
 *
 *   Apos o fix do backend (espaco-exclusivo-backend MR !84 / ADR-022),
 *   as URLs PAR voltam com Content-Disposition: attachment correto e o
 *   `<a download>` cross-origin passou a ser honrado. PORÉM o Chrome
 *   trata multiplos disparos programaticos de download dentro do mesmo
 *   user gesture como "automatic downloads" — bloqueados por default
 *   na configuracao do site, e em maquinas com Enterprise Policy o
 *   bloqueio fica imune a `chrome://settings`.
 *
 *   A unica forma deterministica e cross-browser eh: cada arquivo eh
 *   baixado por um clique de mouse REAL do usuario. Esse modal mostra
 *   N botoes; cada botao eh um `<a download>` cujo clique e um user
 *   gesture novo por definicao — nunca bloqueado.
 *
 * Comportamento:
 *   - Lista os PARs prontos com nome do arquivo, tamanho e botao Baixar.
 *   - Botao Baixar fica desabilitado + check verde apos clicado.
 *   - "Baixar todos" como atalho: dispara N <a download>.click()
 *     programaticos com 100ms entre eles. Funciona se o usuario tiver
 *     permitido downloads automaticos no Chrome; se nao, so o 1o passa
 *     (mesmo cenario do bug original) — usuario ainda pode clicar
 *     manualmente nos demais.
 *   - Falhas (404 do BE — ZIP nao existe no bucket) ficam inline com
 *     o motivo.
 *   - Esc / clique no backdrop fecha o modal sem afetar selecao do
 *     painel.
 */
import React, { useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import Alert from 'react-bootstrap/Alert';

import './index.scss';

export interface DownloadPronto {
  modelo: string;
  url: string;
  filename: string;
  fileSizeBytes: number;
}

export interface DownloadFalha {
  modelo: string;
  motivo: string;
  variante?: string; // 'com_rmv' | 'sem_rmv' — distingue falhas do mesmo modelo (dual-RMV)
}

interface ModalDownloadsProntoProps {
  show: boolean;
  onHide: () => void;
  prontos: DownloadPronto[];
  falhas: DownloadFalha[];
}

const INTERVALO_BAIXAR_TODOS_MS = 150;

function formatarMb(bytes: number): string {
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function totalBytes(itens: DownloadPronto[]): number {
  return itens.reduce((acc, x) => acc + x.fileSizeBytes, 0);
}

/**
 * Dispara o download programatico via <a download> click. Mesmo padrao
 * usado pelo BotaoDownload (compartilhado por consistencia). Em multi-
 * download, alguns browsers podem bloquear do 2o em diante mesmo com
 * autorizacao — por isso o "Baixar todos" eh best-effort.
 */
function triggerDownload(url: string, filename: string): void {
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener noreferrer';
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

async function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

export default function ModalDownloadsPronto({
  show,
  onHide,
  prontos,
  falhas,
}: ModalDownloadsProntoProps) {
  // Index dos itens ja baixados (botao Baixar clicado pelo usuario).
  // Reset toda vez que o modal abre — assim usuario que reabre apos uma
  // nova selecao no painel ve estado limpo.
  const [baixados, setBaixados] = useState<Set<number>>(new Set());

  const marcarBaixado = (idx: number) => {
    setBaixados((prev) => {
      const novo = new Set(prev);
      novo.add(idx);
      return novo;
    });
  };

  const onClickBaixarUm = (idx: number, url: string, filename: string) => {
    triggerDownload(url, filename);
    marcarBaixado(idx);
  };

  const onClickBaixarTodos = async () => {
    // Dispara cada click com pequeno intervalo. Browsers que permitiram
    // downloads multiplos baixam todos; os que nao permitiram baixam
    // so o 1o e o usuario ainda pode clicar manualmente nos demais
    // (botao fica habilitado ate o usuario clicar nele).
    for (let i = 0; i < prontos.length; i += 1) {
      if (baixados.has(i)) continue; // eslint-disable-line no-continue
      const { url, filename } = prontos[i];
      triggerDownload(url, filename);
      marcarBaixado(i);
      if (i < prontos.length - 1) {
        // eslint-disable-next-line no-await-in-loop
        await sleep(INTERVALO_BAIXAR_TODOS_MS);
      }
    }
  };

  const onHideReset = () => {
    setBaixados(new Set());
    onHide();
  };

  const todosBaixados = prontos.length > 0 && baixados.size === prontos.length;
  const totalMb = formatarMb(totalBytes(prontos));

  return (
    <Modal
      show={show}
      onHide={onHideReset}
      size="lg"
      centered
      aria-labelledby="windx-modal-downloads-titulo"
      data-testid="windx-modal-downloads-pronto"
      className="windx-modal-downloads-pronto"
    >
      <Modal.Header closeButton>
        <Modal.Title id="windx-modal-downloads-titulo" className="h6 mb-0">
          Downloads prontos
          {prontos.length > 0 && (
            <span className="text-muted ms-2">
              {`· ${prontos.length} arquivo${prontos.length === 1 ? '' : 's'} · ${totalMb}`}
            </span>
          )}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {prontos.length === 0 && falhas.length === 0 && (
          <p className="text-muted mb-0">Nenhum download disponivel.</p>
        )}

        {prontos.length > 0 && (
          <ul className="windx-modal-downloads-pronto__lista">
            {prontos.map((item, idx) => {
              const baixado = baixados.has(idx);
              return (
                <li
                  key={`${item.modelo}-${item.filename}`}
                  className={
                    `windx-modal-downloads-pronto__item${baixado ? ' is-baixado' : ''}`
                  }
                >
                  <div className="windx-modal-downloads-pronto__info">
                    <strong>{item.modelo}</strong>
                    <code className="windx-modal-downloads-pronto__filename">
                      {item.filename}
                    </code>
                    <span className="windx-modal-downloads-pronto__tamanho">
                      {formatarMb(item.fileSizeBytes)}
                    </span>
                  </div>
                  <Button
                    size="sm"
                    variant={baixado ? 'outline-success' : 'primary'}
                    onClick={() => onClickBaixarUm(idx, item.url, item.filename)}
                    disabled={baixado}
                    data-testid={`baixar-${idx}`}
                  >
                    {baixado ? '✓ Baixado' : 'Baixar'}
                  </Button>
                </li>
              );
            })}
          </ul>
        )}

        {falhas.length > 0 && (
          <Alert
            variant="warning"
            className="mt-3 mb-0 py-2 px-3 small"
            data-testid="modal-downloads-falhas"
          >
            <strong>
              {`${falhas.length} arquivo${falhas.length === 1 ? '' : 's'} indisponive${falhas.length === 1 ? 'l' : 'is'}:`}
            </strong>
            <ul className="mb-0 mt-1 ps-3">
              {falhas.map((f) => (
                <li key={`${f.modelo}-${f.variante ?? 'unica'}`}>
                  <code>{f.modelo}</code>
                  {`: ${f.motivo}`}
                </li>
              ))}
            </ul>
          </Alert>
        )}
      </Modal.Body>
      <Modal.Footer>
        {prontos.length > 1 && (
          <Button
            size="sm"
            variant="outline-primary"
            onClick={onClickBaixarTodos}
            disabled={todosBaixados}
            data-testid="modal-downloads-baixar-todos"
          >
            Baixar todos
          </Button>
        )}
        <Button size="sm" variant="secondary" onClick={onHideReset}>
          Fechar
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
